'use client';

import React, { type MouseEvent, type ReactNode } from 'react';
import {
    Button,
    ButtonContent,
    resolveNoTextPadding,
    type BaseButtonProps,
} from '@milanote/roma/components/base/buttons/button';
import { cx } from '@milanote/roma/utils/cx';
import { TransitionButtonContent } from './transition-button-content';
import type { ButtonTransition } from './transition-button-content';
import { useButtonTransition, type UseButtonTransitionOptions } from './use-button-transition';

export type TransitionButtonContentConfig = Pick<BaseButtonProps, 'children' | 'iconLeading' | 'iconTrailing'>;

type ButtonTransitionTone = 'success' | 'error';

type TransitionButtonTimingOptions = Pick<UseButtonTransitionOptions, 'holdDuration' | 'transitionDuration'>;

export type TransitionButtonProps = Omit<BaseButtonProps, 'ref'> &
    TransitionButtonTimingOptions & {
        /** Visual transition used when swapping between idle and transition content. */
        transition?: ButtonTransition;
        /** Visual tone applied while the transition content is visible. Default: success. */
        transitionTone?: ButtonTransitionTone;
        /** Button-like content shown while the button is in its transition state. */
        transitionContent?: TransitionButtonContentConfig;
        /** Callback when the visual transition has fully completed. */
        onTransitionComplete?: () => void;
    };

type ButtonColor = NonNullable<BaseButtonProps['color']>;

type ButtonSize = NonNullable<BaseButtonProps['size']>;

const iconCurrent =
    '[&_[data-icon]]:text-current hover:[&_[data-icon]]:text-current disabled:[&_[data-icon]]:text-current';

const toneVariants = {
    success: {
        solid: 'bg-success-solid text-white hover:bg-success-solid disabled:bg-success-solid disabled:text-white',
        solidRing:
            'bg-success-solid text-white ring-transparent hover:bg-success-solid hover:text-white disabled:bg-success-solid disabled:text-white disabled:ring-transparent',
        subtle: 'bg-utility-success-100 text-utility-success-700 hover:bg-utility-success-100 hover:text-utility-success-700 disabled:bg-utility-success-100 disabled:text-utility-success-700',
        link: 'text-utility-success-700 hover:text-utility-success-700 disabled:text-utility-success-700',
    },
    error: {
        solid: 'bg-error-solid text-white hover:bg-error-solid disabled:bg-error-solid disabled:text-white',
        solidRing:
            'bg-error-solid text-white ring-transparent hover:bg-error-solid hover:text-white disabled:bg-error-solid disabled:text-white disabled:ring-transparent',
        subtle: 'bg-utility-error-100 text-utility-error-700 hover:bg-utility-error-100 hover:text-utility-error-700 disabled:bg-utility-error-100 disabled:text-utility-error-700',
        link: 'text-utility-error-700 hover:text-utility-error-700 disabled:text-utility-error-700',
    },
};

const createTransitionVariantClasses = (variant: (typeof toneVariants)[ButtonTransitionTone]) => ({
    primary: cx(variant.solid, iconCurrent),
    secondary: cx(variant.solidRing, iconCurrent),
    tertiary: cx(variant.subtle, iconCurrent),
    'primary-destructive': cx(variant.solid, iconCurrent),
    'secondary-destructive': cx(variant.solidRing, iconCurrent),
    'tertiary-destructive': cx(variant.subtle, iconCurrent),
    'link-gray': cx(variant.link, iconCurrent),
    'link-color': cx(variant.link, iconCurrent),
    'link-destructive': cx(variant.link, iconCurrent),
});

const transitionVariantClasses: Record<ButtonTransitionTone, Record<ButtonColor, string>> = {
    success: createTransitionVariantClasses(toneVariants.success),
    error: createTransitionVariantClasses(toneVariants.error),
};

const iconDisabled = 'disabled:[&_[data-icon]]:text-fg-disabled_subtle';
const iconColor = {
    primary: '[&_[data-icon]]:text-button-primary-icon hover:[&_[data-icon]]:text-button-primary-icon_hover',
    quaternary: '[&_[data-icon]]:text-fg-quaternary hover:[&_[data-icon]]:text-fg-quaternary_hover',
    brand: '[&_[data-icon]]:text-fg-brand-primary hover:[&_[data-icon]]:text-fg-brand-primary_hover',
    destructivePrimary:
        '[&_[data-icon]]:text-button-destructive-primary-icon hover:[&_[data-icon]]:text-button-destructive-primary-icon_hover',
    destructiveSecondary: '[&_[data-icon]]:text-fg-error-secondary hover:[&_[data-icon]]:text-fg-error-primary',
};

const nestedIconColorClasses: Record<ButtonColor, string> = {
    primary: cx(iconColor.primary, iconDisabled),
    secondary: cx(iconColor.quaternary, iconDisabled),
    tertiary: cx(iconColor.quaternary, iconDisabled),
    'link-gray': cx(iconColor.quaternary, iconDisabled),
    'link-color': cx(iconColor.brand, iconDisabled),
    'primary-destructive': cx(iconColor.destructivePrimary, iconDisabled),
    'secondary-destructive': cx(iconColor.destructiveSecondary, iconDisabled),
    'tertiary-destructive': cx(iconColor.destructiveSecondary, iconDisabled),
    'link-destructive': cx(iconColor.destructiveSecondary, iconDisabled),
};

const contentGapClasses: Record<ButtonSize, string> = {
    sm: 'gap-1',
    md: 'gap-1 in-data-input-wrapper:gap-1.5',
    lg: 'gap-1.5',
    xl: 'gap-1.5',
};

/** Renders one button content state with consistent spacing and padding. */
const renderButtonContent = (content: TransitionButtonContentConfig, noTextPadding: boolean): ReactNode => (
    <ButtonContent iconLeading={content.iconLeading} iconTrailing={content.iconTrailing} noTextPadding={noTextPadding}>
        {content.children}
    </ButtonContent>
);

/** A Roma Button with one stable root and animated transition-state content. */
export const TransitionButton = React.forwardRef<HTMLButtonElement, TransitionButtonProps>(
    (
        {
            children,
            className,
            color = 'primary',
            size = 'sm',
            iconLeading,
            iconTrailing,
            transition = 'slide',
            transitionTone = 'success',
            transitionContent,
            holdDuration,
            transitionDuration,
            onTransitionComplete,
            onClick,
            isDisabled,
            isLoading,
            showTextWhileLoading,
            style,
            ...buttonProps
        },
        ref,
    ) => {
        const { phase, trigger, isActive, transitionProps } = useButtonTransition({
            holdDuration,
            transitionDuration,
            onComplete: onTransitionComplete,
        });
        const isButtonDisabled = Boolean(isDisabled || isLoading || isActive);
        const isInteractionDisabled = isButtonDisabled;
        const isIconOnlyButton = Boolean((iconLeading || iconTrailing) && !children);

        const resolvedNoTextPadding = resolveNoTextPadding(color, buttonProps.noTextPadding);

        const idleContent = renderButtonContent({ children, iconLeading, iconTrailing }, resolvedNoTextPadding);
        const resolvedTransitionContent = renderButtonContent(
            transitionContent ?? { children, iconLeading, iconTrailing },
            resolvedNoTextPadding,
        );

        const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
            if (isInteractionDisabled) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }

            await onClick?.(event);
            trigger();
        };

        return (
            <Button
                {...buttonProps}
                {...transitionProps}
                ref={ref}
                color={color}
                size={size}
                aria-disabled={isActive || buttonProps['aria-disabled'] || undefined}
                data-icon-only={isIconOnlyButton || undefined}
                className={cx(
                    'overflow-hidden transition-colors',
                    nestedIconColorClasses[color],
                    isActive && 'pointer-events-none',
                    (phase === 'exiting' || phase === 'success') && transitionVariantClasses[transitionTone][color],
                    className,
                )}
                isDisabled={isButtonDisabled}
                isLoading={isLoading}
                showTextWhileLoading={showTextWhileLoading}
                noTextPadding
                onClick={handleClick}
                style={{
                    transitionDuration: 'var(--button-transition-duration)',
                    ...transitionProps.style,
                    ...style,
                }}
            >
                <TransitionButtonContent
                    phase={phase}
                    transition={transition}
                    idleContent={idleContent}
                    transitionContent={resolvedTransitionContent}
                    className={contentGapClasses[size]}
                />
            </Button>
        );
    },
);

TransitionButton.displayName = 'TransitionButton';
